// frontend/src/lib/regwatchGrouping.ts
//
// Local day-bucket grouping for the Regulatory Watch page. Mirrors the
// Today / Yesterday / locale-label logic from changelogFormat.ts but is defined
// LOCALLY here so the Regulatory Watch code never imports changelogFormat.ts
// (kept decoupled — another PR edits that module).
//
// Candidates are bucketed on their `detectedDate`; the input order WITHIN each
// day is preserved exactly (callers pass a pre-sorted list), and the day groups
// themselves are returned newest-day-first.

import i18n from "../i18n";
import type { RuleChangeCandidate } from "./types";

export interface DetectedDateGroup {
  /** Localised header label: "Today" / "Yesterday" / e.g. "10 May 2026". */
  label: string;
  /** YYYY-MM-DD local-date key (or "unknown" for unparseable dates). */
  dateKey: string;
  /** Candidates detected on this day, in the order they were passed in. */
  items: RuleChangeCandidate[];
}

/** Returns a YYYY-MM-DD string in LOCAL time (not UTC). */
function toLocalDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Groups candidates into day buckets keyed on `detectedDate`, newest day first.
 *
 * @param candidates  Candidates pre-sorted for in-group order (e.g. by sortCandidates).
 * @param lang        i18n.language string, e.g. "en" or "fr" (for the date label).
 */
export function groupByDetectedDate(
  candidates: RuleChangeCandidate[],
  lang: string,
): DetectedDateGroup[] {
  if (candidates.length === 0) return [];

  const todayKey = toLocalDateKey(new Date());
  const yesterdayDate = new Date();
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);
  const yesterdayKey = toLocalDateKey(yesterdayDate);

  const buckets = new Map<string, RuleChangeCandidate[]>();

  for (const candidate of candidates) {
    let dateKey: string;
    try {
      const d = new Date(candidate.detectedDate);
      dateKey = isNaN(d.getTime()) ? "unknown" : toLocalDateKey(d);
    } catch {
      dateKey = "unknown";
    }
    if (!buckets.has(dateKey)) buckets.set(dateKey, []);
    buckets.get(dateKey)!.push(candidate);
  }

  // Sort day keys descending (newest first); "unknown" sinks to the bottom.
  const sortedKeys = Array.from(buckets.keys()).sort((a, b) => {
    if (a === "unknown") return 1;
    if (b === "unknown") return -1;
    return b.localeCompare(a);
  });

  return sortedKeys.map((dateKey) => {
    let label: string;
    if (dateKey === todayKey) {
      label = i18n.t("audit:day_today", { defaultValue: "Today" });
    } else if (dateKey === yesterdayKey) {
      label = i18n.t("audit:day_yesterday", { defaultValue: "Yesterday" });
    } else if (dateKey === "unknown") {
      label = "—";
    } else {
      try {
        // Parse the YYYY-MM-DD key as a LOCAL date — `new Date("YYYY-MM-DD")`
        // parses as UTC midnight, which formats one day behind in negative-UTC
        // timezones. Build from local Y/M/D so the label matches the bucket.
        const [y, m, d] = dateKey.split("-").map(Number);
        label = new Date(y, m - 1, d).toLocaleDateString(lang, {
          day: "numeric",
          month: "short",
          year: "numeric",
        });
      } catch {
        label = dateKey;
      }
    }
    return { label, dateKey, items: buckets.get(dateKey)! };
  });
}
